<template>
  <div class="invite-view">
    <van-sticky z-index="99">
      <div class="header">
        <div class="header-content">
          <h1 class="page-title">Invite Friends</h1>
          <p class="page-subtitle">Build your team & earn more</p>
        </div>
        <div class="stats-btn" @click="$router.push({ name: 'Statistics' })">
          <van-icon name="chart-trending-o" />
          <span>Stats</span>
        </div>
      </div>
    </van-sticky>

    <div class="content-wrapper">
      <!-- Invite Reward Card -->
      <div class="reward-card">
        <div class="reward-content">
          <div class="reward-main">
            <span class="reward-percent">10%</span>
            <div class="reward-text">
              <span class="reward-title">Extra Commission</span>
              <span class="reward-desc">For every friend you invite</span>
            </div>
          </div>
          <div class="invite-action">
            <div class="referral-code">
              <span class="label">Your ID:</span>
              <span class="code">{{ user.userId }}</span>
            </div>
            <van-button 
              type="primary" 
              round 
              size="small" 
              class="copy-btn"
              @click="copyInviteLink"
            >
              Copy Invite
            </van-button>
          </div>
        </div>
        <div class="reward-decor">
          <van-icon name="friends-o" class="decor-icon" />
        </div>
      </div>

      <div class="team-section">
        <div class="section-header">
          <h2 class="section-title">My Team</h2>
          <span class="member-count">{{ teamsdata.length }} Members</span>
        </div>

        <van-list
            v-model:loading="loading"
            :finished="finished"
            loading-text="Loading..."
            finished-text="No more team members"
            @load="onLoad"
            class="team-list"
        >
          <div v-for="(team, index) in teamsdata" :key="index" class="team-card animate-fade-in" :style="{ animationDelay: index * 0.1 + 's' }">
            <div class="team-info">
              <div class="avatar-wrapper">
                <van-image round width="44px" height="44px" :src="imageUrl(team.avatar)" />
                <div class="level-badge">L1</div>
              </div>
              <div class="member-details">
                <span class="member-id">UID {{ team.id }}</span>
                <span class="join-date">Joined recently</span>
              </div>
            </div>
            <div class="earnings">
              <span class="earn-label">Commission</span>
              <span class="earn-amount">+{{ team.commission || 0 }} {{ getCurrency() }}</span>
            </div>
          </div>
          
          <div v-if="!loading && teamsdata.length === 0" class="empty-state">
            <van-icon name="friends-o" size="64" />
            <p>Your team is empty. Start inviting!</p>
          </div>
        </van-list>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { showToast } from 'vant'
import { getCurrency, getUserInfo, teams } from '@/utils/api'
import { imageUrl } from '@/utils'

const teamsdata = ref([])
const user = ref({})
const loading = ref(false)
const finished = ref(false)

const fetchUserData = async () => {
  try {
    const res = await getUserInfo()
    user.value = res.data
  } catch (error) {
    console.error(error)
  }
}

const onLoad = async () => {
  try {
    loading.value = true
    const res = await teams()
    teamsdata.value = res.data || []
    finished.value = true
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

const copyInviteLink = () => {
  const link = `${window.location.origin}/#/register?ref=${user.value.userId}`
  navigator.clipboard.writeText(link).then(() => {
    showToast('Invite link copied')
  })
}

onMounted(() => {
  fetchUserData()
})
</script>

<style scoped>
.invite-view {
  background: #f8f9fa;
  min-height: 100vh;
  padding-bottom: 90px;
}

.header {
  background: #fff;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
}

.page-title {
  font-size: 24px;
  font-weight: 800;
  margin: 0;
  color: #1a1a1a;
}

.page-subtitle {
  font-size: 13px;
  color: #8e8e93;
  margin: 4px 0 0;
}

.stats-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: #ff9f00;
  background: #fff9f0;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.stats-btn .van-icon {
  font-size: 18px;
}

.content-wrapper {
  padding: 20px;
}

.reward-card {
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 28px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  color: #fff;
  margin-bottom: 30px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

.reward-content {
  position: relative;
  z-index: 2;
}

.reward-main {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.reward-percent {
  font-size: 42px;
  font-weight: 900;
  color: #ff9f00;
  line-height: 1;
}

.reward-text {
  display: flex;
  flex-direction: column;
}

.reward-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 2px;
}

.reward-desc {
  font-size: 12px;
  opacity: 0.6;
}

.invite-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 12px 16px;
  border-radius: 16px;
  backdrop-filter: blur(5px);
}

.referral-code {
  display: flex;
  flex-direction: column;
}

.referral-code .label {
  font-size: 10px;
  opacity: 0.5;
  text-transform: uppercase;
}

.referral-code .code {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 1px;
}

.copy-btn {
  background: #ff9f00;
  border: none;
  font-weight: 700;
  color: #000;
  height: 32px;
  padding: 0 16px;
}

.reward-decor {
  position: absolute;
  top: -20px;
  right: -20px;
  opacity: 0.1;
  transform: rotate(-15deg);
}

.decor-icon {
  font-size: 120px;
}

.team-section {
  background: transparent;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 4px;
}

.section-title {
  font-size: 20px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
}

.member-count {
  font-size: 13px;
  color: #8e8e93;
  font-weight: 600;
}

.team-card {
  background: #fff;
  border-radius: 20px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

.team-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-wrapper {
  position: relative;
}

.level-badge {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: #4cd964;
  color: #fff;
  font-size: 8px;
  font-weight: 800;
  padding: 2px 4px;
  border-radius: 4px;
  border: 2px solid #fff;
}

.member-details {
  display: flex;
  flex-direction: column;
}

.member-id {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
}

.join-date {
  font-size: 11px;
  color: #c7c7cc;
}

.earnings {
  text-align: right;
}

.earn-label {
  display: block;
  font-size: 10px;
  color: #8e8e93;
  margin-bottom: 2px;
}

.earn-amount {
  font-size: 16px;
  font-weight: 800;
  color: #ff9f00;
}

.empty-state {
  text-align: center;
  padding: 60px 0;
  color: #c7c7cc;
}

.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}

.animate-fade-in {
  animation: fadeIn 0.5s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
